/**
 * @fileoverview Profile Detection Hook
 * 
 * Provides functionality to check if an authenticated user has a creator profile
 * and determine appropriate routing (onboarding vs dashboard).
 * 
 * Requirements:
 * - 3.1: Query CreatorProfiles table to check for existing profiles
 * - 3.4: Handle API errors gracefully without blocking login
 * - 3.5: Display loading indicator during profile check
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCreatorDashboardApiClient } from './useCreatorDashboard';

export interface UseProfileDetectionReturn {
  isLoading: boolean;
  hasProfile: boolean | null;
  profileCount: number | null;
  redirectTo: 'onboarding' | 'dashboard' | null;
  error: string | null;
  retry: () => Promise<void>;
}

/**
 * Hook to detect if user has a creator profile and determine routing
 * 
 * @returns Profile detection state and retry function
 */
export function useProfileDetection(): UseProfileDetectionReturn {
  const { isAuthenticated } = useAuth();
  const apiClient = useCreatorDashboardApiClient();
  
  const [isLoading, setIsLoading] = useState(true);
  const [hasProfile, setHasProfile] = useState<boolean | null>(null);
  const [profileCount, setProfileCount] = useState<number | null>(null);
  const [redirectTo, setRedirectTo] = useState<'onboarding' | 'dashboard' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const checkProfile = useCallback(async () => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Call the profile check API
      const response = await apiClient.checkCreatorProfile();

      // Update state with response
      setHasProfile(response.hasProfile);
      setProfileCount(response.profileCount);
      setRedirectTo(response.redirectTo);
    } catch (err) {
      // Log error but don't block user (Requirement 3.4)
      console.error('Failed to check creator profile:', err);
      
      // Set error message
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to check profile status');
      }

      // Reset profile state on error
      setHasProfile(null);
      setProfileCount(null);
      setRedirectTo(null);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, apiClient]);

  // Check profile on mount when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      checkProfile();
    } else {
      setIsLoading(false);
    }
  }, [isAuthenticated, checkProfile]);

  // Provide retry function for error recovery
  const retry = useCallback(async () => {
    await checkProfile();
  }, [checkProfile]);

  return {
    isLoading,
    hasProfile,
    profileCount,
    redirectTo,
    error,
    retry,
  };
}
